import { ChartModel, ChartType, GridOptions } from 'ag-grid-community';
import { Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { AgGridChartRef } from 'src/shared/models/ag-grid-chart.models';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AgGridIChartTemplateService } from './ag-grid-ichart-template.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-ag-grid-ichart-template',
    templateUrl: './ag-grid-ichart-template.component.html',
    styleUrls: ['./ag-grid-ichart-template.component.scss']
})
export class AgGridIChartTemplateComponent implements OnInit {
    @ViewChild('agIChart1') agIChart1: ElementRef<HTMLDivElement>;
    @ViewChild('agIChart2') agIChart2: ElementRef<HTMLDivElement>;
    @ViewChild('ichartTemplateGrid') agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    public isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    public isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.

    public data: any;

    public gridOptions: GridOptions;
    private chartRefs: Array<AgGridChartRef> = new Array();  // Keeps track of the rendered chart. Required if the chart needs to be re-rendered.
    public filterValue: string = "";
    public showFilter: boolean = false;

    protected gridState: any;
    private chartStates: Array<ChartModel>;
    protected stateSaveTimer: number;

    public showGrid = true;
    public showGraph = true;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        protected agGridHelperService: AgGridHelperService,
        private agGridIChartTemplateService: AgGridIChartTemplateService,
        private zone: NgZone,
        private configService: CvConfigurationService
    ) {


        this.cvLocalizationService.GetSection("Template_Module").finally(() => {
            this.SetupGrid();
        });
    }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        // Module Configuration:
        this.moduleManager.setOptions({
            // name: '' // sets the title of the module (defaults to the database entry's ModuleName, if unset).
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: settings => {
                // this function will be called when the settings are loaded for the first time (for existing modules)
                //  and also when the user clicks the apply button in the settings modal

                this.LoadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true, // hasRefresh determines if a module has a refresh menu item, this item will call onRefresh when clicked
            // in general refreshType should not be changed, it will be determined automatically based upon metrics you have.
            // if it does need to be changed, see Module Container's moduleOptions object for more information
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => {
                // isAutoRefresh is set to true when the timer is causing the reload.  It's false when the user clicks the refresh button themself.
                // this function will be called when the refresh interval for this module is hit
                // this can be called before the module is ready for it, so any checking to determine if
                //  it is actually ready to refresh should be done here
                // example:
                // if (this.dataLoaded)
                //    loadData(); // load data again
                // note: loadData() should have code to make sure we don't show a loading animation and instead just update the data we already have
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                return this.LoadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
        });

        //#region Menu Items

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.ToggleOpenSearch(),
        });

        this.moduleManager.addMenuItem({
            name: 'Module Display',
            icon: 'chartType',
            type: 'subMenu',
            getEnabled: () => true,
            subItems: [{
                name: "Table",
                icon: 'tableView',
                click: () => this.zone.run(() => this.changeModes(true, false)),
            },
            {
                name: "Chart",
                icon: 'chartView',
                click: () => this.zone.run(() => this.changeModes(false, true)),
            },
            {
                name: "Table and Chart",
                icon: 'tableAndChartView',
                click: () => this.zone.run(() => this.changeModes(true, true)),
            }]
        } as ModuleManagerSubMenu);

        //#endregion

        //#region Event Handlers
        // Example code for gaining/losing focus, can be removed
        // See Module Container's registerEvents() for more on event handlers
        this.moduleManager.registerEvents({
            onGainedFocus: () => {
                // this.enableAutoRefresh = true;
            },
            onLostFocus: () => {
                // this.enableAutoRefresh = false;
            }
        });
        //#endregion

        //#region States and Settings
        // States - used for saving values that are not set in settings
        //  example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => {
                return this.gridState;
            },
            onStateLoaded: stateData => {
                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });

        this.moduleManager.addState({
            name: "chartState",
            getStateData: () => this.chartStates,
            onStateLoaded: stateData => {
                if (!stateData)
                    return;

                this.chartStates = stateData;
                if (this.agGrid?.api && this.chartStates?.length)
                    this.chartRefs = this.agGridHelperService.loadMultipleChartStates(this.agGrid?.api, this.chartRefs, this.chartStates);
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });
        // Settings - used for adding custom settings to the settings dialog
        //  requires a [Template]Settings directive (see _TemplateSettings.js/aspx)
        //#endregion
    }

    protected async LoadData(isAutoRefresh: boolean): Promise<void> {
        this.data = this.agGridIChartTemplateService.GetData();
        if (this.agGrid?.api)
            this.agGrid?.api.setGridOption('rowData', this.data);
        window.setTimeout(() => {
            this.LoadData(false);
        }, 10000);
    }

    private SetupGrid(): void {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            context: { moduleManager: this.moduleManager },
            columnDefs: [
                { field: 'product', chartDataType: 'category' },
                { field: 'book', chartDataType: 'category' },

                { field: 'current', type: 'measure' },
                { field: 'previous', type: 'measure' },
                { headerName: 'PL 1', field: 'pl1', type: 'measure' },
                { headerName: 'PL 2', field: 'pl2', type: 'measure' },
                { headerName: 'Gain-DX', field: 'gainDx', type: 'measure' },
                { headerName: 'SX / PX', field: 'sxPx', type: 'measure' },

                { field: 'trade', type: 'measure' },
                { field: 'submitterID', type: 'measure' },
                { field: 'submitterDealID', type: 'measure', minWidth: 170 },

                { field: 'portfolio' },
                { field: 'dealType' },
                { headerName: 'Bid', field: 'bidFlag' }
            ],
            defaultColDef: {
                editable: true,
                sortable: true,
                flex: 1,
                minWidth: 150,
                filter: true,
                resizable: true,
                icons: this.configService.isCxOne ? {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                }: null,
            },
            columnTypes: {
                measure: {
                    chartDataType: 'series',
                    cellClass: 'number',
                    valueFormatter: this.numberCellFormatter,
                    cellRenderer: 'agAnimateShowChangeCellRenderer'
                }
            },
            animateRows: true,
            enableCharts: true,
            suppressAggFuncInHeader: true,
            getRowId: ({data}) => data.trade,
            onFirstDataRendered: params => {
                if (!this.chartRefs)
                    this.chartRefs = new Array();

                this.chartRefs.push(this.agGridHelperService.createChart(
                    this.agGrid?.api,
                    this.chartRefs[0],
                    {
                        chartContainer: this.agIChart1.nativeElement,
                        cellRange: {
                            columns: this.agGrid?.api.getColumns()
                        },
                        chartType: 'pie',
                        chartThemeOverrides: {
                            common: {
                                title: {
                                    enabled: true,
                                    fontSize: 22,
                                    fontFamily: 'Arial, sans-serif',
                                    text: "Test Tables"
                                }
                            }
                        },
                    }
                ));

                this.chartRefs.push(this.agGridHelperService.createChart(
                    this.agGrid?.api,
                    this.chartRefs[1],
                    {
                        chartContainer: this.agIChart2.nativeElement,
                        cellRange: {
                            columns: this.agGrid?.api.getColumns()
                        },
                        chartType: 'groupedBar',
                        chartThemeOverrides: {
                            common: {
                                title: {
                                    enabled: true,
                                    fontSize: 22,
                                    fontFamily: 'Arial, sans-serif',
                                    text: "Test Tables"
                                }
                            }
                        },
                    }
                ));

                if (this.chartStates)
                    this.agGridHelperService.loadMultipleChartStates(this.agGrid?.api, this.chartRefs, this.chartStates);
            },
            onGridReady: (event) => {
                if (this.data)
                    event.api.setGridOption('rowData', this.data);

                if (this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
            },
            onChartOptionsChanged: event => this.queueStateSave(),
            onChartRangeSelectionChanged: event => this.queueStateSave(),
            onSortChanged: event => this.queueStateSave(),
            onColumnMoved: event => this.queueStateSave(),
            onColumnPinned: event => this.queueStateSave(),
            onColumnVisible: event => {
                event.api.sizeColumnsToFit();
                this.queueStateSave();
            },
            rowData: []
        }, {
            enableChart: true,
            enableCopy: true
        });
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api) {
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
                this.chartStates = this.agGridHelperService.getChartState(this.agGrid?.api);
            }
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500/*ms*/);
    }

    // Called by AngularJS when the search bar's value has changed.
    public ApplyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.filterValue = this.filterValue.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public ClearFilter() {
        this.filterValue = "";
        this.ApplyFilter();
    }

    /**
     * Shows and hides the search/filter bar
     */
    private ToggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.ClearFilter();
        } else {
            this.showFilter = true;
        }
    }

    private numberCellFormatter(params) {
        return Math.floor(params.value).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    }

    private changeModes(table: boolean = true, graph: boolean = true) {
        this.showGrid = table;
        this.showGraph = graph;
    }
}
